import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ContactlessIcon from '@mui/icons-material/Contactless';
import QrScannerDialog from './QrScannerDialog';
import NfcScanDialog from './NfcScanDialog';
import { isNfcSupported } from './nfcLogin';

interface LoginPageProps {
    login: (username: string, password: string) => Promise<string | null>;
    loginWithToken: (token: string) => Promise<string | null>;
    /** Why an automatic login (a tapped NFC card) failed before this screen. */
    initialError?: string | null;
}

export default function LoginPage({
    login,
    loginWithToken,
    initialError
}: LoginPageProps) {
    const intl = useIntl();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(initialError ?? null);
    const [loading, setLoading] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [nfcOpen, setNfcOpen] = useState(false);
    const nfcSupported = isNfcSupported();

    async function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        setError(null);
        setLoading(true);

        const loginError = await login(username, password);
        if (loginError) {
            setError(loginError);
            setLoading(false);
        }
    }

    /** Log in with a token off a QR code or an NFC card — same credential. */
    async function redeemToken(token: string) {
        setScanOpen(false);
        setNfcOpen(false);
        setError(null);
        setLoading(true);
        const loginError = await loginWithToken(token);
        if (loginError) {
            setError(loginError);
            setLoading(false);
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default'
            }}
        >
            <Stack
                spacing={1.5}
                sx={{
                    maxWidth: 400,
                    width: '100%',
                    mx: 2,
                    alignItems: 'center'
                }}
            >
                <Card sx={{ width: '100%' }}>
                    <CardContent>
                        <Box
                            component="img"
                            src="/ast-logo.svg"
                            alt={intl.formatMessage({ id: 'app.name' })}
                            sx={{
                                height: (theme) => theme.spacing(18),
                                maxWidth: '100%',
                                display: 'block',
                                mx: 'auto',
                                my: 3
                            }}
                        />

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <TextField
                                    label={
                                        <FormattedMessage id="auth.username" />
                                    }
                                    value={username}
                                    onChange={(event) =>
                                        setUsername(event.target.value)
                                    }
                                    required
                                    fullWidth
                                    autoFocus
                                />
                                <TextField
                                    label={
                                        <FormattedMessage id="auth.password" />
                                    }
                                    type="password"
                                    value={password}
                                    onChange={(event) =>
                                        setPassword(event.target.value)
                                    }
                                    required
                                    fullWidth
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    disabled={loading || !username || !password}
                                >
                                    {loading ? (
                                        <FormattedMessage id="auth.login.loading" />
                                    ) : (
                                        <FormattedMessage id="auth.login.submit" />
                                    )}
                                </Button>
                            </Stack>
                        </form>

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={1}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<QrCodeScannerIcon />}
                                onClick={() => setScanOpen(true)}
                                disabled={loading}
                            >
                                <FormattedMessage id="auth.qr.scanButton" />
                            </Button>
                            {nfcSupported && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ContactlessIcon />}
                                    onClick={() => setNfcOpen(true)}
                                    disabled={loading}
                                >
                                    <FormattedMessage id="auth.nfc.scanButton" />
                                </Button>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
                {/* Dimmed, but there: a login problem is reported with the version
                it happened in, before anyone can reach the settings. */}
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    <FormattedMessage
                        id="settings.version"
                        values={{ version: __APP_VERSION__ }}
                    />
                </Typography>
            </Stack>

            {scanOpen && (
                <QrScannerDialog
                    open
                    onClose={() => setScanOpen(false)}
                    onDecoded={redeemToken}
                />
            )}
            {nfcOpen && (
                <NfcScanDialog
                    open
                    onClose={() => setNfcOpen(false)}
                    onToken={redeemToken}
                />
            )}
        </Box>
    );
}
